import _ from 'lodash';
import { colors, createTheme, responsiveFontSizes } from '@material-ui/core';

import { THEMES } from '~/constants';
import { softShadows, strongShadows } from './shadows';
import typography from './typography';
import overrides from './overrides';

const baseOptions = {
  direction: 'ltr',
  typography,
  overrides
};

const themesOptions = [
  {
    name: THEMES.DEFAULT,
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: colors.blueGrey[600]
          }
        },
        root: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(128, 128, 128, 0.05)'
          }
        }
      }
    },
    palette: {
      type: 'light',
      action: {
        active: colors.blueGrey[600],
        hover: 'rgba(0, 0, 0, 0.04)',
        selected: 'rgba(0, 0, 0, 0.08)',
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        focus: 'rgba(0, 0, 0, 0.12)'
      },
      divider: 'rgba(0, 0, 0, 0.14)',
      background: {
        default: colors.common.white,
        dark: '#eceff1',
        darkest: '#D5D5D5',
        paper: colors.common.white
      },
      primary: {
        main: 'rgb(0,104,233)',
        mainLightBg: 'rgb(227, 238, 252)',
        contrastText: '#f4f6f8',
      },
      secondary: {
        main: '#29abe2'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50',
        light: '#4caf50',
        dark: '#4caf50'
      },
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[700],
        contrast: '#e6e5e8',
      },
      listItem: {
        row: {
          hoverColor: 'rgba(0, 0, 0, 0.06)',
          separatorColor: 'rgb(128, 128, 128, 1)',
        },
        scrollbar: {
          bgTransparent: 'rgba(255,255,255,0.0)',
          bgColor1: 'rgba(0, 0, 0, 0.15)',
          bgColor2: 'rgba(0, 0, 0, 0.25)',
          bgColor3: 'rgba(0, 0, 0, 0.35)',
        }
      },
    },
    shadows: softShadows
  },
  {
    name: THEMES.DARK,
    overrides: {
      MuiInputBase: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(128, 128, 128, 0.05)'
          }
        }
      }
    },
    palette: {
      type: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)'
      },
      divider: 'rgba(255, 255, 255, 0.14)',
      background: {
        default: '#282C34',
        dark: '#1c2025',
        darkest: '#212121',
        paper: '#282C34'
      },
      primary: {
        main: 'rgb(0,104,233)',
        mainLightBg: 'rgb(227, 238, 252)',
        contrastText: '#e6e5e8',
      },
      secondary: {
        main: '#29abe2'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50',
        light: '#4caf50',
        dark: '#4caf50'
      },
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      text: {
        primary: '#e6e5e8',
        secondary: '#adb0bb',
        contrast: colors.blueGrey[900],
      }
    },
    shadows: strongShadows
  },
  {
    name: THEMES.UNICORN,
    palette: {
      type: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)'
      },
      background: {
        default: '#2a2d3d',
        dark: '#222431',
        paper: '#2a2d3d'
      },
      primary: {
        main: '#0068e9'
      },
      secondary: {
        main: '#a67dff'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#f6f5f8',
        secondary: '#9699a4'
      }
    },
    shadows: strongShadows
  }
];

export const createMuiTheme = (config = {}) => {
  let themeOptions = themesOptions.find(theme => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  let theme = createTheme (
    _.merge({}, baseOptions, themeOptions, { direction: config.direction })
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
